import { PlayerApiPayload, PlayerContent } from '@foundation-player/player-api';
import {
  AdvertisingSlotConfigType,
  EncryptionType,
  ManifestPriority,
  ManifestType,
  SourceType,
} from '@foundation-player/runtime';
import { AdBumperClip, AdvertisingSlotConfig, MediaConfig } from '@foundation-player/types';

type PlayerAPIAdClip = {
  /**
   * clip url*
   *
   * @example
   * 'https://watch-advertising.now-plus-prod.aws-cbc.cloud/rtl_plus_opener.mp4'*
   *
   * @example
   * 'https://watch-advertising.now-plus-prod.aws-cbc.cloud/rtl_plus_closer.mp4'*
   *
   * @example
   * 'https://watch-advertising.now-plus-prod.aws-cbc.cloud/rtl_plus_bumper.mp4'
   */
  url: string;

  /**
   * should the clip also be played if the ad server does not provide a slot
   */
  forcePlay?: boolean;

  /**
   * type of clip
   */
  type: 'OPENER' | 'CLOSER' | 'BUMPER';
};

/**
 * Maps a PlayerApiPayload to a MediaConfig
 *
 * @remarks
 * This function must be called before creating the player to generate a MediaConfig
 */
export function mapToMediaConfig(playerApiPayload: PlayerApiPayload): MediaConfig {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (!playerApiPayload.playerContent) {
    throw new Error('Content is required to transform to media config! Missing "content" in playerApiPayload');
  }

  const thumbnailManifest = (playerApiPayload.playerContent.manifests || []).find((manifest) => manifest.thumbnails);
  const thumbnails = thumbnailManifest?.thumbnails;

  const heartbeatConfig = playerApiPayload.playerContent.modules?.heartbeat;
  const googleAnalyticsConfig = playerApiPayload.playerContent.modules?.googleAnalytics;
  const npawAnalyticsConfig = playerApiPayload.playerContent.modules?.npawAnalytics;

  return {
    type: mapSourceType(playerApiPayload.playerContent.type),
    id: playerApiPayload.playerContent.id,
    autoplay: playerApiPayload.playerContent.flags?.autoStart,
    muted: playerApiPayload.playerContent.flags?.muted,
    playbackSpeed: playerApiPayload.playerContent.flags?.playbackSpeed,
    // audioLanguage: input.playerMedia.?, // This is not available in the input yet
    // textTrackLanguage: input.playerMedia.?, // This is not available in the input yet
    thumbnails: thumbnails
      ? {
          url: thumbnails,
          preload: false,
        }
      : undefined,
    startTime: playerApiPayload.playerContent.timestamps?.startTime,
    advertising: {
      slots: (playerApiPayload.playerContent.modules?.smartclip?.slots || []).map(
        (slot) =>
          ({
            type: mapAdSlotType(slot.type),
            adCall: slot.adCall,
            offset: slot.offset,
            prefetch: slot.prefetch,
            skippable: slot.skipDelay,
            bumpers: mapStaticAdClipConfig(findStaticAdClips(slot.clips || [], 'BUMPER')),
            openers: mapStaticAdClipConfig(findStaticAdClips(slot.clips || [], 'OPENER')),
            closers: mapStaticAdClipConfig(findStaticAdClips(slot.clips || [], 'CLOSER')),
          }) satisfies AdvertisingSlotConfig,
      ),
    },
    manifests: (playerApiPayload.playerContent.manifests || []).map((manifest) => ({
      type: mapManifestType(manifest.name),
      sources: manifest.sources.map((source) => ({
        url: source.url,
        priority: source.priority === 'MAIN' ? ManifestPriority.Main : ManifestPriority.Fallback,
        isYospace: source.isYospace,
      })),
      licenseServers: (manifest.licenses || []).map((license) => ({
        encryptionType: mapEncryptionType(license.type),
        address: license.address,
        certificateUrl: license.certificate,
      })),
    })),
    metadata: {
      headline: playerApiPayload.playerUiContent?.headline,
      secondaryHeadline: playerApiPayload.playerUiContent?.secondaryHeadline,
      poster: playerApiPayload.playerUiContent?.posterImage,
    },
    plugins: {
      'foundation.plugin.npawAnalytics': npawAnalyticsConfig
        ? {
            title: npawAnalyticsConfig.title,
            program: npawAnalyticsConfig.program,
            season: npawAnalyticsConfig.season,
            channelName: npawAnalyticsConfig.channelName,
          }
        : undefined,
      'foundation.plugin.googleAnalytics4': googleAnalyticsConfig
        ? {
            // contentType: googleAnalyticsConfig.contentType, entspricht nicht bisherigem spec
            editorialSources: googleAnalyticsConfig.editorialSources,
            editorialUnit: googleAnalyticsConfig.editorialUnit,
            embedType: googleAnalyticsConfig.embedType,
            firstHeadline: googleAnalyticsConfig.firstHeadline,
            firstPublicationDate: googleAnalyticsConfig.firstPublicationDate,
            lastPublicationDate: googleAnalyticsConfig.lastPublicationDate,
            headline: googleAnalyticsConfig.headline,
            playlist: googleAnalyticsConfig.playlist,
            tags: googleAnalyticsConfig.tags,
            keywords: googleAnalyticsConfig.keywords,
          }
        : undefined,
      'foundation.plugin.heartbeat': heartbeatConfig
        ? {
            ivw: '/developer/default',
            startType: mapHeartbeatStartType(heartbeatConfig.startType),
          }
        : undefined,
    },
  };
}

function mapHeartbeatStartType(inputType: 'USER' | 'AUTO' | 'PLAYLIST' | string | undefined): number {
  switch (inputType) {
    case 'USER':
      return 0;
    case 'AUTO':
      return 1;
    case 'PLAYLIST':
      return 2;
    default:
      // when in doubt, default to user interaction
      return 0;
  }
}

function findStaticAdClips(clips: PlayerAPIAdClip[], type: PlayerAPIAdClip['type']): PlayerAPIAdClip[] {
  return clips.filter((clip) => clip.type === type);
}

function mapStaticAdClipConfig(input: PlayerAPIAdClip[]): AdBumperClip[] {
  return input.map((clip) => ({
    url: clip.url,
    forcePlay: clip.forcePlay,
  }));
}

function mapAdSlotType(inputType: 'NON_LINEAR' | 'LINEAR' | string): AdvertisingSlotConfigType {
  switch (inputType) {
    case 'NON_LINEAR':
      return AdvertisingSlotConfigType.NonLinear;
    case 'LINEAR':
      return AdvertisingSlotConfigType.Linear;
  }

  // This should never happen because the switch is exhaustive,
  // but in case this does happen, we want to throw an error
  throw new Error(`Unknown ad slot type: ${inputType}`);
}

function mapEncryptionType(inputType: 'WIDEVINE' | 'FAIRPLAY' | 'PLAYREADY' | string): EncryptionType {
  switch (inputType) {
    case 'WIDEVINE':
      return EncryptionType.Widevine;
    case 'FAIRPLAY':
      return EncryptionType.FairPlay;
    case 'PLAYREADY':
      return EncryptionType.PlayReady;
  }

  // This should never happen because the switch is exhaustive,
  // but in case this does happen, we want to throw an error
  throw new Error(`Unknown encryption type: ${inputType}`);
}

function mapManifestType(
  inputType: 'dashsd' | 'dashhd' | 'hlsfairplaysd' | 'hlsfairplayhd' | 'progressive' | string,
): ManifestType {
  switch (inputType) {
    case 'dashsd':
      return ManifestType.DashSD;
    case 'dashhd':
      return ManifestType.DashHD;
    case 'hlsfairplaysd':
      return ManifestType.HlsSD;
    case 'hlsfairplayhd':
      return ManifestType.HlsHD;
    case 'progressive':
      return ManifestType.Progressive;
  }

  // This should never happen because the switch is exhaustive,
  // but in case this does happen, we want to throw an error
  throw new Error(`Unknown manifest type: ${inputType}`);
}

function mapSourceType(inputType: PlayerContent['type']): SourceType {
  switch (inputType) {
    case 'VOD':
      return SourceType.VOD;
    case 'LIVE':
      return SourceType.Live;
    case 'EVENT':
      return SourceType.Event;
    case 'FAST':
      return SourceType.FAST;
    case 'RADIO':
      return SourceType.Radio;
    case 'PODCAST':
      return SourceType.Podcast;
    case 'AUDIOBOOK':
      return SourceType.Audiobook;
    case 'AD_ONLY':
      throw new Error('AD_ONLY is not supported as a source type! Please use the default HTML5 api instead!');
  }

  // This should never happen because the switch is exhaustive,
  // but in case this does happen, we want to throw an error
  throw new Error(`Unknown source type: ${inputType}`);
}
